import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  ActionIcon,
  Badge,
  Button,
  Center,
  Container,
  Group,
  Loader,
  Modal,
  Stack,
  Table,
  Text,
  Tooltip,
} from '@mantine/core';
import { IconPencil, IconPlus, IconTrash } from '@tabler/icons-react';

import { LogoPaper, PageTitle } from '@/components';
import { useRiskProfiles } from '@/hooks';

/**
 * Admin list of risk profiles — the rating policies screening outcomes are
 * judged by. The platform default is always first: the built-in rules until
 * an admin edits them, then a profile row like any other (flagged default,
 * never deletable).
 */
export function RiskProfilesPage() {
  const navigate = useNavigate();
  const { profiles, platformDefault, isLoading, error, deleteProfile } = useRiskProfiles();
  const [pendingDelete, setPendingDelete] = useState<{ id: string; name: string } | null>(null);

  const confirmDelete = (id: string, name: string) => setPendingDelete({ id, name });

  if (isLoading && profiles.length === 0 && !platformDefault) {
    return (
      <Center h={300}>
        <Loader />
      </Center>
    );
  }

  const named = profiles.filter((p) => !p.isPlatformDefault);

  return (
    <Container size="xl" py="md">
      <Modal
        opened={pendingDelete !== null}
        onClose={() => setPendingDelete(null)}
        title="Delete risk profile"
      >
        <Stack>
          <Text size="sm">
            Delete <b>{pendingDelete?.name}</b>? Report configs cannot point at a deleted profile,
            so this is only allowed while none do.
          </Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setPendingDelete(null)}>
              Keep
            </Button>
            <Button
              color="red"
              onClick={() => {
                if (pendingDelete) {
                  void deleteProfile(pendingDelete.id);
                }
                setPendingDelete(null);
              }}
            >
              Delete
            </Button>
          </Group>
        </Stack>
      </Modal>
      <Stack>
        <Group justify="space-between" align="flex-start">
          <Stack gap={4}>
            <PageTitle title="Risk profiles" />
            <Text size="sm" c="dimmed" maw={720}>
              A profile is an ordered list of rules: each finding remediation retains takes the
              rating of the first rule it matches, a subject is rated by its worst finding and a
              report by its worst subject. Report configs point at a profile; everything else uses
              the platform default. Editing a profile re-rates every report it governs.
            </Text>
          </Stack>
          <Button
            leftSection={<IconPlus size={16} />}
            onClick={() => navigate('/admin/risk-profiles/new')}
          >
            New profile
          </Button>
        </Group>

        {error ? <Text c="red">Could not load risk profiles: {error.message}</Text> : null}

        <LogoPaper>
          <Table highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Profile</Table.Th>
                <Table.Th>Slug</Table.Th>
                <Table.Th>Rules</Table.Th>
                <Table.Th>Version</Table.Th>
                <Table.Th>Configs using</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              <Table.Tr
                style={{ cursor: 'pointer' }}
                onClick={() => navigate('/admin/risk-profiles/platform-default')}
                data-testid="risk-profile-row-platform-default"
              >
                <Table.Td>
                  <Group gap="xs">
                    <Text fw={500}>{platformDefault?.name ?? 'Platform default'}</Text>
                    <Badge size="xs" variant="light">
                      Default
                    </Badge>
                    {platformDefault?.builtin ? (
                      <Tooltip label="The shipped rules — no edits yet.">
                        <Badge size="xs" variant="outline" color="gray">
                          Built-in
                        </Badge>
                      </Tooltip>
                    ) : null}
                  </Group>
                </Table.Td>
                <Table.Td>
                  <Text size="sm" c="dimmed">
                    platform-default
                  </Text>
                </Table.Td>
                <Table.Td>{platformDefault?.rules.length ?? '—'}</Table.Td>
                <Table.Td>{platformDefault?.version ?? '—'}</Table.Td>
                <Table.Td>
                  <Text size="sm" c="dimmed">
                    every config without a profile
                  </Text>
                </Table.Td>
                <Table.Td onClick={(e) => e.stopPropagation()}>
                  <Tooltip label="Edit">
                    <ActionIcon
                      variant="subtle"
                      onClick={() => navigate('/admin/risk-profiles/platform-default')}
                    >
                      <IconPencil size={16} />
                    </ActionIcon>
                  </Tooltip>
                </Table.Td>
              </Table.Tr>
              {named.map((profile) => (
                <Table.Tr
                  key={profile.id}
                  style={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/admin/risk-profiles/${profile.id}`)}
                >
                  <Table.Td>
                    <Text fw={500}>{profile.name}</Text>
                    {profile.description ? (
                      <Text size="xs" c="dimmed" lineClamp={1}>
                        {profile.description}
                      </Text>
                    ) : null}
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed">
                      {profile.slug}
                    </Text>
                  </Table.Td>
                  <Table.Td>{profile.rules.length}</Table.Td>
                  <Table.Td>{profile.version}</Table.Td>
                  <Table.Td>{profile.configsUsing}</Table.Td>
                  <Table.Td onClick={(e) => e.stopPropagation()}>
                    <Group gap={4} wrap="nowrap">
                      <Tooltip label="Edit">
                        <ActionIcon
                          variant="subtle"
                          aria-label={`Edit ${profile.name}`}
                          onClick={() => navigate(`/admin/risk-profiles/${profile.id}`)}
                        >
                          <IconPencil size={16} />
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip
                        label={
                          profile.configsUsing > 0
                            ? 'In use by report configs — reassign them first'
                            : 'Delete'
                        }
                      >
                        <ActionIcon
                          variant="subtle"
                          color="red"
                          aria-label={`Delete ${profile.name}`}
                          disabled={profile.configsUsing > 0}
                          onClick={() => confirmDelete(profile.id, profile.name)}
                        >
                          <IconTrash size={16} />
                        </ActionIcon>
                      </Tooltip>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </LogoPaper>
      </Stack>
    </Container>
  );
}
